import MoreVertIcon from '@mui/icons-material/MoreVert';
import { AvatarGroup, Link, Stack, useTheme } from '@mui/material';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AttachmentInput from '../attachment/attachment.input';
import AttachmentList from '../attachment/attachment.list';
import { useMenu } from '../common/dialog.util';
import { lighter } from '../common/util';
import { DialogsContext } from '../contexts';
import SubjectAvatar from '../subject/subject.avatar';
import { courseApi } from './course.queries';
import StudentAvatar from '../student/student.avatar';

export default function CourseCard(props) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const useGet = courseApi.useGet(props.id, { enabled: !props.course });
	const course = props.course || useGet?.data;

	const useDelete = courseApi.useDelete();

	const theme = useTheme();
	const menu = useMenu();
	const { dialogs } = useContext(DialogsContext);

	if (!course?.name) {
		return;
	}

	const onSelect = async (file) => {
		course.attachments = course.attachments ? course.attachments : [];
		course.attachments.push(file);
	};

	const onUpload = async (file) => {
		_.remove(course.attachments, {fileName: file.fileName});
		course.attachments.push(file);
		queryClient.invalidateQueries(['course', course.id]);
	};
	
	const onDeleteAttachment = async(attachment) => {
		_.remove(course.attachments, { id: attachment.id });
		queryClient.invalidateQueries(['course', course.id]);
	};

	const onDelete = async () => {
		await useDelete.mutateAsync(course.id);
		if (window.location.pathname.startsWith('/courses/' + course.id)) {
			navigate('/courses');
		}
	};

	const href = course?.id ? '/courses/' + course.id : null;
	const linkActive = window.location.pathname != href;

	const optionsMenu = (
		<Box>
			<IconButton className={menu.isOpen ? '' : 'hover-muted'} onClick={(e) => menu.open(e)} sx={{ mr: -1 }}>
				<MoreVertIcon />
			</IconButton>
			<Menu open={menu.isOpen} onClose={() => menu.close()} anchorEl={menu.anchor}>
				<MenuItem onClick={() => { menu.close(); dialogs.open('course', course) }}>Edit</MenuItem>
				<MenuItem component="label" role={undefined}>
					Upload Attachment
					<AttachmentInput attachableType="course" attachableId={course.id} onSelect={(file) => { menu.close(); onSelect(file) }} onUpload={(file) => onUpload(file)} />
				</MenuItem>
				<Divider />
				<MenuItem onClick={() => { menu.close(); onDelete(course.id) }}>Delete</MenuItem>
			</Menu>
		</Box>
	);

	let childrenLabel = _.chain(course.courseDetails).countBy('type').map((value, key) => value + ' ' + key + 's').join(', ').value();
	const nowrapSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: "ellipsis", '&:hover .MuiTypography-h6': { textDecoration: 'underline' } };

	const cardContent = (
		// <CardContent sx={{ p: 2, position: 'relative', flexGrow: 1 }}>
		<CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
			<Stack sx={{ pb: 2 }} spacing={1}>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<SubjectAvatar subject={course.subject} size={24} />
				</Box>
				<Link underline="none" onClick={() => navigate(href)} sx={nowrapSx}>
					<Stack direction="column" spacing={1} sx={{ flex: 1 }}>
						<Typography variant="h6" sx={nowrapSx} title={course?.name}>{course.name}</Typography>
						<Stack spacing={4}>
							<Typography variant="subtitle2">{course.grade}</Typography>
							<Typography variant="subtitle2">{childrenLabel}</Typography>
						</Stack>
					</Stack>
				</Link>
				<Box sx={{ flex: 1 }}></Box>
				{props.showAvatars && 
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<AvatarGroup max={3} sx={{ ml: 2 }}>
							{course.enrollments?.map((enrollment) => (
								<StudentAvatar key={enrollment.id} student={enrollment.student} enrollment={enrollment} />
							))}
						</AvatarGroup>
					</Box>
				}
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					{!props.hideMenu && optionsMenu}
				</Box>
			</Stack>
		</CardContent>
	);

	const colour = course.colour || course.subject?.colour || '#81c784';
	const cardSx = { width: '100%', background: lighter(colour, 0.8, theme) };

	return (
		<Card sx={cardSx} className="hover-toggle">
			{/* {cardMedia} */}
			{cardContent}
			{props.showAttachments == true && course.attachments?.length > 0 &&
				<Box sx={{ p: 1 }}>
					<Divider sx={{ mb: 1 }} />
					<AttachmentList attachments={course.attachments} onDelete={(attachment) => onDeleteAttachment(attachment)} />
				</Box>
			}
		</Card>
	);
}